import React, { useState } from 'react';
import Button from '../controls/Button';
import { ClipboardDocumentCheckIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

interface SQLSetupSectionProps {
  setupSQL: string;
  accountIdentifier: string;
  sqlExecuted: boolean;
  onSqlExecutedChange: (executed: boolean) => void;
}

/**
 * Display SQL commands with copy functionality and Snowsight integration
 *
 * Features:
 * - Display SQL in a code block with monospace font
 * - Copy button for SQL commands
 * - "Open in Snowsight" button that opens Snowflake web UI
 * - Checkbox: "I have run this SQL in Snowflake"
 *
 * @param setupSQL - The SQL commands to display
 * @param accountIdentifier - For constructing Snowsight URL
 * @param sqlExecuted - Whether the SQL has been executed
 * @param onSqlExecutedChange - Callback when checkbox state changes
 */
export function SQLSetupSection({
  setupSQL,
  accountIdentifier,
  sqlExecuted,
  onSqlExecutedChange,
}: SQLSetupSectionProps) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  const handleCopySQL = async () => {
    try {
      await navigator.clipboard.writeText(setupSQL);
      setCopied(true);
      setCopyError(false);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy SQL:', err);
      setCopyError(true);
      setTimeout(() => {
        setCopyError(false);
      }, 3000);
    }
  };

  const handleOpenSnowsight = () => {
    const snowsightUrl = `https://${accountIdentifier}.snowflakecomputing.com/`;
    window.open(snowsightUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-4">
      {/* Open in Snowsight Button - moved above SQL block */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          intent="secondary"
          size="large"
          onClick={handleOpenSnowsight}
          className="flex-1 sm:flex-initial"
          aria-label="Open Snowflake Snowsight interface in new tab"
        >
          <ArrowTopRightOnSquareIcon className="w-4 h-4 mr-2" aria-hidden="true" />
          Open Snowsight
        </Button>
      </div>

      {/* SQL Code Block */}
      <div className="relative">
        <div className="rounded-md border border-gray-300 bg-gray-50 p-4 overflow-auto max-h-48">
          <pre className="text-sm font-mono text-gray-900 whitespace-pre-wrap break-all">{setupSQL}</pre>
        </div>

        {/* Copy Button - positioned at top right of code block */}
        <div className="absolute top-2 right-2">
          <Button
            intent="secondary"
            size="large"
            onClick={handleCopySQL}
            className=""
            aria-label="Copy SQL commands to clipboard"
          >
            <ClipboardDocumentCheckIcon className="w-5 h-5 mr-2" />
            {copied ? 'Copied!' : copyError ? 'Failed' : 'Copy SQL'}
          </Button>
        </div>
      </div>

      {/* Confirmation Checkbox */}
      <div className="flex items-start">
        <div className="flex items-center h-5">
          <input
            id="sql-executed"
            type="checkbox"
            checked={sqlExecuted}
            onChange={(e) => {
              onSqlExecutedChange(e.target.checked);
            }}
            className="h-4 w-4 rounded border-gray-300 text-dataops-primary-light-blue focus:ring-dataops-primary-light-blue cursor-pointer"
            aria-describedby="sql-executed-description"
          />
        </div>
        <div className="ml-3 text-sm">
          <label htmlFor="sql-executed" className="font-medium text-gray-700 cursor-pointer">
            I have run this SQL in Snowflake
          </label>
          <p id="sql-executed-description" className="text-gray-500">
            Check this box after successfully executing the SQL commands in your Snowflake account.
          </p>
        </div>
      </div>
    </div>
  );
}
